import { useLingui } from '@lingui/react/macro';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Typography } from '@mui/material';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { memo, useCallback, useMemo, useState } from 'react';
import { hoverMenuItemTabIndex } from '../hover_menu.tsx';
import {
  NavMenuItemButton,
  navMenuItemDescriptionSx,
  navMenuItemExternalIconSx,
  navMenuItemHeaderSx,
  navMenuItemIconSx,
} from '../nav_hover_group/nav_menu_item_styles.ts';
import { type DisplayNavItem, useFilteredNavItems } from '../useFilteredNavItems.ts';
import { getToolsItemMeta } from './config.tsx';

interface ToolsPanelProps {
  open: boolean;
  onNavigate?: () => void;
}

export const ToolsPanel = memo(function ToolsPanel({ open, onNavigate }: ToolsPanelProps) {
  const { t } = useLingui();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [hoveredKey, setHoveredKey] = useState<number | null>(null);
  const filteredNavItems = useFilteredNavItems();
  const overflowItems = useMemo(() => filteredNavItems.filter((item) => item.overflow), [filteredNavItems]);

  const handleItemClick = useCallback(
    (item: DisplayNavItem) => {
      onNavigate?.();
      if (item.external) {
        window.open(item.path, '_blank', 'noopener,noreferrer');
        return;
      }
      void navigate({ to: item.path });
    },
    [navigate, onNavigate]
  );

  if (!overflowItems.length) return null;

  return (
    <>
      {overflowItems.map((item) => {
        const { Icon, description } = getToolsItemMeta(item);
        const routeActive = !item.external && (item.path === pathname || pathname.startsWith(`${item.path}/`));
        return (
          <NavMenuItemButton
            key={item.value}
            isHighlighted={hoveredKey === item.value || routeActive}
            onMouseEnter={() => setHoveredKey(item.value)}
            onFocus={() => setHoveredKey(item.value)}
            onClick={() => handleItemClick(item)}
            tabIndex={hoverMenuItemTabIndex(open)}
            disableRipple
          >
            <Typography component="span" sx={navMenuItemHeaderSx}>
              <Icon sx={navMenuItemIconSx} />
              {item.label}
              {item.external ? <OpenInNewIcon fontSize="xSmall" sx={navMenuItemExternalIconSx} /> : null}
            </Typography>
            <Typography sx={navMenuItemDescriptionSx}>{description ? t(description) : item.path}</Typography>
          </NavMenuItemButton>
        );
      })}
    </>
  );
});
